import { defineRouting } from "next-intl/routing";
import { createNavigation } from "next-intl/navigation";

export const routing = defineRouting({
  // "en" sells to the US, "en-GB" to the UK under /uk (never the code "uk": that is Ukrainian).
  locales: ["nl", "de", "en", "en-GB"],
  defaultLocale: "nl",
  localePrefix: { mode: "always", prefixes: { "en-GB": "/uk" } },
  localeDetection: false,
  // No NEXT_LOCALE cookie: with localeDetection off nothing reads it, and a Set-Cookie on every
  // response kept the landing pages out of the edge cache (lib/edgeCache.ts).
  localeCookie: false,
  // No HTTP `Link: rel="alternate"` header. next-intl derives its x-default from the unprefixed
  // path (/prijzen, /upload), which contradicted the <link rel="alternate"> tags in the HTML
  // (built by i18n/alternates.ts) on every page. One source of hreflang, the HTML.
  alternateLinks: false,
  pathnames: {
    "/": "/",
    "/upload": "/upload",
    // The motorcycle ad lander. Kept separate from the home page while both are still being
    // learned; they can merge once the moto angles have a baseline.
    "/motorcycle-poster": {
      nl: "/motorposter",
      de: "/motorradposter",
      en: "/motorcycle-poster",
      "en-GB": "/motorcycle-poster",
    },
    // The pets line: its own lander and its own funnel, reached from the lander and from the pets
    // tile on the /upload question.
    "/pets": {
      nl: "/huisdieren",
      de: "/haustiere",
      en: "/pets",
      "en-GB": "/pets",
    },
    // Christmas cards: US only, so one English path. The page 404s on /nl and /de.
    "/christmas-cards": "/christmas-cards",
    // The family card's own funnel: who is on it, the snowy scene, the greeting. Then the shared
    // orientation, processing and preview steps.
    "/card-family": "/christmas-cards/family",
    "/card-family-scene/[id]": "/christmas-cards/family/scene/[id]",
    "/card-family-greeting/[id]": "/christmas-cards/family/greeting/[id]",
    "/pet-upload": {
      nl: "/huisdieren/upload",
      de: "/haustiere/upload",
      en: "/pets/upload",
      "en-GB": "/pets/upload",
    },
    "/pet-style/[id]": {
      nl: "/huisdieren/stijl/[id]",
      de: "/haustiere/stil/[id]",
      en: "/pets/style/[id]",
      "en-GB": "/pets/style/[id]",
    },
    "/pet-scene/[id]": {
      nl: "/huisdieren/plek/[id]",
      de: "/haustiere/ort/[id]",
      en: "/pets/place/[id]",
      "en-GB": "/pets/place/[id]",
    },
    "/pet-pose/[id]": {
      nl: "/huisdieren/houding/[id]",
      de: "/haustiere/pose/[id]",
      en: "/pets/pose/[id]",
      "en-GB": "/pets/pose/[id]",
    },
    "/pet-text/[id]": {
      nl: "/huisdieren/tekst/[id]",
      de: "/haustiere/text/[id]",
      en: "/pets/text/[id]",
      "en-GB": "/pets/text/[id]",
    },
    "/processing/[id]": "/processing/[id]",
    "/preview/[id]": "/preview/[id]",
    "/checkout/[id]": "/checkout/[id]",
    // Same path in every locale: it is only ever reached from a support link, and a short one is
    // easier to paste into a chat than a translated word.
    "/cart": "/cart",
    "/order/[id]": "/order/[id]",
    "/my-designs": {
      nl: "/mijn-ontwerpen",
      de: "/meine-entwuerfe",
      en: "/my-designs",
      "en-GB": "/my-designs",
    },
    // The account pages keep ONE path in every locale, like /cart above. Two of them are reached
    // from a link in an email the backend builds — the verification and reset links — and a
    // translated path there is a 404 for anyone whose mail was written in a different locale than
    // the one they open it in.
    "/account": "/account",
    "/account/sign-in": "/account/sign-in",
    "/account/sign-up": "/account/sign-up",
    "/account/forgot-password": "/account/forgot-password",
    "/account/reset-password": "/account/reset-password",
    "/account/verify": "/account/verify",
    "/styles": {
      nl: "/styles",
      de: "/stile",
      en: "/styles",
      "en-GB": "/styles",
    },
    "/style/[id]": {
      nl: "/stijl/[id]",
      de: "/stil/[id]",
      en: "/style/[id]",
      "en-GB": "/style/[id]",
    },
    "/theme/[id]": {
      nl: "/thema/[id]",
      de: "/motiv/[id]",
      en: "/theme/[id]",
      "en-GB": "/theme/[id]",
    },
    "/look/[id]": "/look/[id]",
    "/rider/[id]": {
      nl: "/rijder/[id]",
      de: "/fahrer/[id]",
      en: "/rider/[id]",
      "en-GB": "/rider/[id]",
    },
    "/people/[id]": {
      nl: "/personen/[id]",
      de: "/personen/[id]",
      en: "/people/[id]",
      "en-GB": "/people/[id]",
    },
    "/state/[id]": {
      nl: "/actie/[id]",
      de: "/aktion/[id]",
      en: "/state/[id]",
      "en-GB": "/state/[id]",
    },
    "/mood/[id]": {
      nl: "/sfeer/[id]",
      de: "/stimmung/[id]",
      en: "/mood/[id]",
      "en-GB": "/mood/[id]",
    },
    "/light/[id]": {
      nl: "/licht/[id]",
      de: "/licht/[id]",
      en: "/light/[id]",
      "en-GB": "/light/[id]",
    },
    "/text/[id]": {
      nl: "/tekst/[id]",
      de: "/text/[id]",
      en: "/text/[id]",
      "en-GB": "/text/[id]",
    },
    "/orientation/[id]": {
      nl: "/orientatie/[id]",
      de: "/format/[id]",
      en: "/orientation/[id]",
      "en-GB": "/orientation/[id]",
    },
    "/review": {
      nl: "/review",
      de: "/bewertung",
      en: "/review",
      "en-GB": "/review",
    },
    "/reviews": "/reviews",
    "/about": {
      nl: "/about",
      de: "/ueber-uns",
      en: "/about",
      "en-GB": "/about",
    },
    "/pricing": {
      nl: "/prijzen",
      de: "/preise",
      en: "/pricing",
      "en-GB": "/pricing",
    },
    "/terms": {
      nl: "/voorwaarden",
      de: "/agb",
      en: "/terms",
      "en-GB": "/terms",
    },
    "/returns": {
      nl: "/herroeping",
      de: "/widerruf",
      en: "/returns",
      "en-GB": "/returns",
    },
    "/privacy": {
      nl: "/privacy",
      de: "/datenschutz",
      en: "/privacy",
      "en-GB": "/privacy",
    },
    "/impressum": "/impressum",
    // The SEO content pages (src/content/seo, rendered by components/seo). English-only, apart from
    // /nl/cadeau/*; each 404s in the other locales, like /impressum, and the sitemap lists one URL.
    "/custom-car-poster": "/custom-car-poster",
    "/custom-motorcycle-poster": "/custom-motorcycle-poster",
    "/car-photo-guide": "/car-photo-guide",
    "/gifts": "/gifts",
    "/guides": "/guides",
    "/gifts/[slug]": "/gifts/[slug]",
    "/posters": "/posters",
    "/posters/[slug]": "/posters/[slug]",
    "/guides/[slug]": "/guides/[slug]",
    "/pets/memorial-portrait": "/pets/memorial-portrait",
    "/pets/custom-pet-poster": "/pets/custom-pet-poster",
    "/pets/multi-pet-portrait": "/pets/multi-pet-portrait",
    "/pets/breeds/[slug]": "/pets/breeds/[slug]",
    "/cadeau/[slug]": "/cadeau/[slug]",
  },
});

export type AppPathnames = keyof typeof routing.pathnames;

export const { Link, redirect, usePathname, useRouter } = createNavigation(routing);
